/**
 * Qual marcador (M1-M18) e cada mancha achada na foto.
 *
 * Compara as manchas com um "esqueleto modelo": a posicao esperada de cada
 * marcador num cavalo de perfil, em unidades de altura de cernelha (cernelha na
 * origem, solo em y = 1, x crescendo da cabeca para a cauda). O modelo e
 * encaixado na foto (escala, giro, deslocamento) e cada mancha vai para o
 * marcador mais proximo pela atribuicao otima (algoritmo hungaro); encaixe e
 * atribuicao se repetem ate estabilizar. Testa a cabeca a esquerda e a direita
 * e fica com o que explica melhor as manchas.
 *
 * Mancha que nao casa com nada fica de fora (reflexo, fivela); marcador sem
 * mancha fica em branco para o avaliador marcar. Os duvidosos vao em `conferir`.
 */
import type { Ponto } from './fotogrametria.js';
import type { Mancha } from './deteccao-marcadores.js';

/** Posicao esperada e folga (desvio, mesma unidade) de cada marcador. */
export interface PontoDoModelo {
  x: number;
  y: number;
  folga: number;
}

/**
 * Lado esquerdo, cabeca a esquerda da foto. Medido em duas fotos de Mangalarga
 * Marchador (uma de cada lado) e na anatomia. A nuca depende de como a cabeca
 * esta, por isso a folga maior. PROVISORIO: refinar com fotos de campo.
 */
export const ESQUELETO_MODELO: Readonly<Record<string, PontoDoModelo>> = {
  M1: { x: -0.4, y: -0.15, folga: 0.2 },
  M2: { x: 0, y: 0, folga: 0.07 },
  M3: { x: -0.06, y: 0.07, folga: 0.07 },
  M4: { x: -0.25, y: 0.32, folga: 0.07 },
  M5: { x: -0.11, y: 0.43, folga: 0.07 },
  M6: { x: -0.08, y: 0.68, folga: 0.08 },
  M7: { x: -0.07, y: 0.89, folga: 0.08 },
  M8: { x: -0.1, y: 0.97, folga: 0.08 },
  M9: { x: 0.42, y: 0.05, folga: 0.08 },
  M10: { x: 0.56, y: -0.01, folga: 0.07 },
  M11: { x: 0.5, y: 0.1, folga: 0.07 },
  M12: { x: 0.62, y: 0.3, folga: 0.07 },
  M13: { x: 0.69, y: 0.21, folga: 0.07 },
  M14: { x: 0.56, y: 0.4, folga: 0.07 },
  M15: { x: 0.73, y: 0.66, folga: 0.07 },
  M16: { x: 0.69, y: 0.63, folga: 0.07 },
  M17: { x: 0.7, y: 0.87, folga: 0.07 },
  M18: { x: 0.67, y: 0.95, folga: 0.07 },
};

export interface Identificacao {
  /** Codigo do marcador → ponto na foto (0-1, como as manchas). */
  pontos: Record<string, Ponto>;
  /** Marcadores identificados com duvida: o avaliador confere primeiro. */
  conferir: string[];
  /** Lado do animal que aparece: esquerdo = cabeca a esquerda da foto. */
  lado: 'esquerdo' | 'direito';
  /** Manchas que nao casaram com nenhum marcador. */
  sobras: Ponto[];
}

export interface OpcoesDeIdentificacao {
  /** Largura / altura da foto, para medir em pixels de verdade. */
  proporcao: number;
  /** So estes marcadores (ex.: os colados no dia). Padrao: todos do modelo. */
  marcadores?: readonly string[] | undefined;
  modelo?: Readonly<Record<string, PontoDoModelo>> | undefined;
}

/** Transformacao afim: p = [a b; d e]·t + [c; f]. */
interface Afim {
  a: number;
  b: number;
  c: number;
  d: number;
  e: number;
  f: number;
}

const aplicar = (T: Afim, p: Ponto): Ponto => ({ x: T.a * p.x + T.b * p.y + T.c, y: T.d * p.x + T.e * p.y + T.f });
const escala = (T: Afim) => Math.sqrt(Math.abs(T.a * T.e - T.b * T.d));

/** Custo de deixar uma mancha sem marcador, ou um marcador sem mancha. */
const PENALIDADE = 4;
/** Acima deste custo (desvios ao quadrado) o marcador vai para conferencia. */
const CUSTO_DUVIDOSO = 2;

export function identificarMarcadores(manchas: readonly Mancha[], opcoes: OpcoesDeIdentificacao): Identificacao {
  const modelo = opcoes.modelo ?? ESQUELETO_MODELO;
  const codigos = (opcoes.marcadores ?? Object.keys(modelo)).filter((c) => modelo[c]);
  const prop = opcoes.proporcao || 1;
  // Em pixels "quadrados", como em fotogrametria.ts.
  const pts = manchas.map((m) => ({ x: m.x * prop, y: m.y }));
  const vazio: Identificacao = { pontos: {}, conferir: [], lado: 'esquerdo', sobras: manchas.map(({ x, y }) => ({ x, y })) };
  if (pts.length < 3 || codigos.length === 0) return vazio;

  let melhor: (Encaixe & { lado: Identificacao['lado'] }) | null = null;
  for (const lado of ['esquerdo', 'direito'] as const) {
    // Cabeca a direita: espelha o modelo, para a transformacao continuar sem reflexao.
    const sinal = lado === 'esquerdo' ? 1 : -1;
    const alvo = codigos.map((c) => ({ codigo: c, x: sinal * modelo[c]!.x, y: modelo[c]!.y, folga: modelo[c]!.folga }));
    for (const T0 of chutesIniciais(pts, alvo)) {
      const r = encaixar(pts, alvo, T0);
      if (r && (!melhor || r.custo < melhor.custo)) melhor = { ...r, lado };
    }
  }
  if (!melhor) return vazio;

  const pontos: Record<string, Ponto> = {};
  const conferir: string[] = [];
  const usadas = new Set<number>();
  for (const [j, i] of melhor.par) {
    const codigo = melhor.alvo[j]!.codigo;
    usadas.add(i);
    pontos[codigo] = { x: manchas[i]!.x, y: manchas[i]!.y };
    const custo = melhor.custos[i]![j]!;
    // Duvida: longe do esperado, ou tao perto de outro marcador que ficou sem
    // mancha quanto do escolhido (ex.: M15 e M16 quando so um aparece).
    const ambiguo = melhor.alvo.some((_, jk) => jk !== j && !melhor!.par.has(jk) && melhor!.custos[i]![jk]! - custo < CUSTO_DUVIDOSO);
    if (custo > CUSTO_DUVIDOSO || ambiguo) conferir.push(codigo);
  }
  return {
    pontos,
    conferir: codigos.filter((c) => conferir.includes(c)),
    lado: melhor.lado,
    sobras: manchas.filter((_, i) => !usadas.has(i)).map(({ x, y }) => ({ x, y })),
  };
}

interface Alvo extends Ponto {
  codigo: string;
  folga: number;
}

interface Encaixe {
  custo: number;
  /** indice do alvo → indice da mancha */
  par: Map<number, number>;
  custos: number[][];
  alvo: Alvo[];
}

/** Encaixe pela caixa que envolve os pontos, com algumas hipoteses do que esta no topo e nas pontas. */
function chutesIniciais(pts: readonly Ponto[], alvo: readonly Alvo[]): Afim[] {
  const caixa = (ps: readonly Ponto[]) => ({
    x0: Math.min(...ps.map((p) => p.x)),
    x1: Math.max(...ps.map((p) => p.x)),
    y0: Math.min(...ps.map((p) => p.y)),
    y1: Math.max(...ps.map((p) => p.y)),
  });
  const P = caixa(pts);
  const semCabeca = alvo.filter((a) => a.codigo !== 'M1');
  const chutes: Afim[] = [];
  for (const modelo of [alvo, semCabeca]) {
    if (modelo.length < 2) continue;
    const M = caixa(modelo);
    // A altura manda na escala (o comprimento do corpo varia mais entre cavalos).
    const s = (P.y1 - P.y0) / (M.y1 - M.y0 || 1);
    const sx = (P.x1 - P.x0) / (M.x1 - M.x0 || 1);
    for (const k of [s, (s + sx) / 2]) {
      chutes.push({ a: k, b: 0, c: (P.x0 + P.x1) / 2 - k * ((M.x0 + M.x1) / 2), d: 0, e: k, f: P.y1 - k * M.y1 });
    }
  }
  return chutes;
}

function encaixar(pts: readonly Ponto[], alvo: Alvo[], T0: Afim): Encaixe | null {
  let T = T0;
  let anterior = '';
  let resultado: Encaixe | null = null;
  for (let volta = 0; volta < 8; volta++) {
    const s = escala(T);
    if (!(s > 0)) return null;
    const custos = pts.map((p) =>
      alvo.map((a) => {
        const q = aplicar(T, a);
        const d = Math.hypot(p.x - q.x, p.y - q.y) / s / a.folga;
        return d * d;
      }),
    );
    const { par, custo } = atribuir(custos);
    resultado = { custo, par, custos, alvo };
    const chave = [...par].sort((x, y) => x[0] - y[0]).join(';');
    if (chave === anterior || par.size < 2) break;
    anterior = chave;
    // Reencaixe robusto: um par muito ruim (mancha intrusa) nao puxa o modelo.
    const pares = [...par]
      .filter(([j, i]) => custos[i]![j]! <= 2 * CUSTO_DUVIDOSO || par.size < 4)
      .map(([j, i]) => ({ t: alvo[j]!, p: pts[i]!, peso: 1 / alvo[j]!.folga ** 2 }));
    const novo = (pares.length >= 6 && afim(pares)) || similaridade(pares);
    if (!novo) break;
    T = novo;
  }
  if (!resultado) return null;
  // Foto deitada de lado ou de cabeca para baixo nao e perfil de cavalo.
  if (Math.abs(Math.atan2(T.d, T.a)) > (25 * Math.PI) / 180 || T.a <= 0 || T.e <= 0) return null;
  return resultado;
}

interface Par {
  t: Ponto;
  p: Ponto;
  peso: number;
}

/** Escala + giro + deslocamento, minimos quadrados ponderados. */
function similaridade(pares: readonly Par[]): Afim | null {
  const W = pares.reduce((s, q) => s + q.peso, 0);
  if (pares.length < 2 || W === 0) return null;
  const mt = { x: pares.reduce((s, q) => s + q.peso * q.t.x, 0) / W, y: pares.reduce((s, q) => s + q.peso * q.t.y, 0) / W };
  const mp = { x: pares.reduce((s, q) => s + q.peso * q.p.x, 0) / W, y: pares.reduce((s, q) => s + q.peso * q.p.y, 0) / W };
  let A = 0;
  let B = 0;
  let N = 0;
  for (const q of pares) {
    const tx = q.t.x - mt.x;
    const ty = q.t.y - mt.y;
    const px = q.p.x - mp.x;
    const py = q.p.y - mp.y;
    A += q.peso * (tx * px + ty * py);
    B += q.peso * (tx * py - ty * px);
    N += q.peso * (tx * tx + ty * ty);
  }
  if (N === 0) return null;
  const a = A / N;
  const d = B / N;
  return { a, b: -d, c: mp.x - a * mt.x + d * mt.y, d, e: a, f: mp.y - d * mt.x - a * mt.y };
}

/**
 * Afim completa (corpo mais comprido ou mais curto que o modelo, foto um pouco
 * de baixo). Recusada se deformar demais — ai fica a similaridade.
 */
function afim(pares: readonly Par[]): Afim | null {
  // Equacoes normais de [tx ty 1]·[a b c] = px (e o mesmo para y).
  const M = [
    [0, 0, 0],
    [0, 0, 0],
    [0, 0, 0],
  ];
  const vx = [0, 0, 0];
  const vy = [0, 0, 0];
  for (const q of pares) {
    const v = [q.t.x, q.t.y, 1];
    for (let i = 0; i < 3; i++) {
      for (let j = 0; j < 3; j++) M[i]![j]! += q.peso * v[i]! * v[j]!;
      vx[i]! += q.peso * v[i]! * q.p.x;
      vy[i]! += q.peso * v[i]! * q.p.y;
    }
  }
  const sx = resolver3(M, vx);
  const sy = resolver3(M, vy);
  if (!sx || !sy) return null;
  const T: Afim = { a: sx[0], b: sx[1], c: sx[2], d: sy[0], e: sy[1], f: sy[2] };
  const ex = Math.hypot(T.a, T.d);
  const ey = Math.hypot(T.b, T.e);
  const cisalhamento = Math.abs(T.a * T.b + T.d * T.e) / (ex * ey);
  if (T.a * T.e - T.b * T.d <= 0 || ex / ey < 0.8 || ex / ey > 1.25 || cisalhamento > 0.1) return null;
  return T;
}

function resolver3(M: number[][], v: number[]): [number, number, number] | null {
  const det = (m: number[][]) =>
    m[0]![0]! * (m[1]![1]! * m[2]![2]! - m[1]![2]! * m[2]![1]!) -
    m[0]![1]! * (m[1]![0]! * m[2]![2]! - m[1]![2]! * m[2]![0]!) +
    m[0]![2]! * (m[1]![0]! * m[2]![1]! - m[1]![1]! * m[2]![0]!);
  const D = det(M);
  if (Math.abs(D) < 1e-12) return null;
  const col = (k: number) => M.map((linha, i) => linha.map((x, j) => (j === k ? v[i]! : x)));
  return [det(col(0)) / D, det(col(1)) / D, det(col(2)) / D];
}

/**
 * Atribuicao otima manchas × marcadores, com a opcao de deixar qualquer um sem
 * par (custo PENALIDADE). Um par so vale se custar menos que deixar os dois de fora.
 */
function atribuir(custos: number[][]): { par: Map<number, number>; custo: number } {
  const n = custos.length;
  const m = custos[0]?.length ?? 0;
  const N = n + m;
  const C = Array.from({ length: N }, (_, i) =>
    Array.from({ length: N }, (_, j) => {
      if (i < n && j < m) return custos[i]![j]!;
      if (i >= n && j >= m) return 0;
      return PENALIDADE;
    }),
  );
  const linhaDaColuna = hungaro(C);
  const par = new Map<number, number>();
  let custo = 0;
  for (let j = 0; j < N; j++) {
    const i = linhaDaColuna[j]!;
    custo += C[i]![j]!;
    if (i < n && j < m) par.set(j, i);
  }
  return { par, custo };
}

/** Algoritmo hungaro (minimizacao, matriz quadrada). Devolve a linha de cada coluna. */
function hungaro(C: number[][]): number[] {
  const n = C.length;
  const u = new Array<number>(n + 1).fill(0);
  const v = new Array<number>(n + 1).fill(0);
  const p = new Array<number>(n + 1).fill(0);
  const caminho = new Array<number>(n + 1).fill(0);
  for (let i = 1; i <= n; i++) {
    p[0] = i;
    let j0 = 0;
    const minv = new Array<number>(n + 1).fill(Infinity);
    const usado = new Array<boolean>(n + 1).fill(false);
    do {
      usado[j0] = true;
      const i0 = p[j0]!;
      let delta = Infinity;
      let j1 = 0;
      for (let j = 1; j <= n; j++) {
        if (usado[j]) continue;
        const cur = C[i0 - 1]![j - 1]! - u[i0]! - v[j]!;
        if (cur < minv[j]!) {
          minv[j] = cur;
          caminho[j] = j0;
        }
        if (minv[j]! < delta) {
          delta = minv[j]!;
          j1 = j;
        }
      }
      for (let j = 0; j <= n; j++) {
        if (usado[j]) {
          u[p[j]!]! += delta;
          v[j]! -= delta;
        } else minv[j]! -= delta;
      }
      j0 = j1;
    } while (p[j0] !== 0);
    do {
      const j1 = caminho[j0]!;
      p[j0] = p[j1]!;
      j0 = j1;
    } while (j0);
  }
  const linha = new Array<number>(n);
  for (let j = 1; j <= n; j++) linha[j - 1] = p[j]! - 1;
  return linha;
}
